import { useEffect, useState } from "react";
import { isEmbeddedRoute } from "@loan-management/contracts";

import { ApplicationNavigation } from "../components/ApplicationNavigation";
import { EmptyState } from "../components/EmptyState";
import { PageHeading, type BreadcrumbItem } from "../components/PageHeading";
import { AccountingSettingsPage } from "../pages/AccountingSettingsPage";
import { DashboardPage } from "../pages/DashboardPage";
import { LendersPage } from "../pages/LendersPage";
import { LoanDetailPage } from "../pages/LoanDetailPage";
import { LoansPage } from "../pages/LoansPage";
import { NewLoanPage } from "../pages/NewLoanPage";
import { PostingBatchPage } from "../pages/PostingBatchPage";
import { PostingPage } from "../pages/PostingPage";
import { ReconciliationPage } from "../pages/ReconciliationPage";
import { ReconciliationRunPage } from "../pages/ReconciliationRunPage";
import { ReportsPage } from "../pages/ReportsPage";
import { SageSettingsPage } from "../pages/SageSettingsPage";
import { SettingsOverviewPage } from "../pages/SettingsOverviewPage";

interface RouteDefinition {
  breadcrumbs: BreadcrumbItem[];
  description: string;
  emptyMessage: string;
  emptyTitle: string;
  path: string;
  title: string;
}

const routeDefinitions: RouteDefinition[] = [
  defineRoute("/dashboard", "Dashboard", "", "No portfolio activity", "Portfolio values will appear after active loan schedules are available."),
  defineRoute("/lenders", "Lenders", "", "No lenders", "Lenders can be added after Sage vendor synchronization is configured."),
  defineRoute("/lenders/:lenderId", "Lender", "Lender details and related loans.", "Lender details unavailable", "Lender records are introduced in the lender master-data feature.", ["Lenders"]),
  defineRoute("/lender-groups/:lenderGroupId", "Lender group", "Lender-group details and concentration reporting.", "Lender group details unavailable", "Lender groups are introduced in the lender master-data feature.", ["Lenders"]),
  defineRoute("/loans", "Loans", "", "No loans", "Loans can be created after accounting settings and lender master data are available."),
  defineRoute("/loans/new", "New loan", "Create a loan from manually entered contract terms.", "Loan setup unavailable", "Manual loan setup is introduced in the loan-core feature.", ["Loans"]),
  defineLoanRoute("/loans/:loanId", "Loan", "Loan record and available accounting information."),
  defineLoanRoute("/loans/:loanId/edit", "Edit loan", "Edit loan details and create a draft term version when active terms change."),
  defineLoanRoute("/loans/:loanId/overview", "Overview", ""),
  defineLoanRoute("/loans/:loanId/terms", "Terms", ""),
  defineLoanRoute("/loans/:loanId/rates", "Rates", ""),
  defineLoanRoute("/loans/:loanId/fees", "Fees", ""),
  defineLoanRoute("/loans/:loanId/event-types", "Event types", ""),
  defineLoanRoute("/loans/:loanId/accounting", "Accounting", ""),
  defineLoanRoute("/loans/:loanId/amortization", "Amortization", ""),
  defineLoanRoute("/loans/:loanId/payments", "Payments", ""),
  defineLoanRoute("/loans/:loanId/journal-entries", "Journal entries", ""),
  defineLoanRoute("/loans/:loanId/reconciliation", "Reconciliation", ""),
  defineLoanRoute("/loans/:loanId/documents", "Documents", ""),
  defineLoanRoute("/loans/:loanId/audit", "Audit history", ""),
  defineRoute("/posting", "Posting", "", "No events ready to post", "Accounting events will appear after an amortization schedule is activated."),
  defineRoute("/posting/:postingBatchId", "Posting batch", "Posting-batch validation and Sage results.", "Posting batch unavailable", "Posting batches are introduced in the Sage posting feature.", ["Posting"]),
  defineRoute("/reconciliation", "Reconciliation", "", "No reconciliation runs", "Reconciliation becomes available after journal entries have been posted."),
  defineRoute("/reconciliation/:reconciliationRunId", "Reconciliation run", "Reconciliation results and supporting records.", "Reconciliation run unavailable", "Reconciliation runs are introduced in the reconciliation feature.", ["Reconciliation"]),
  defineRoute("/reports", "Reports", "Loan and portfolio reporting views.", "No reporting data", "Reports will use active schedules and posted accounting events when they are available."),
  defineRoute("/settings", "Settings", "", "No settings configured", "Start with Sage connection and accounting settings when those features are available."),
  defineSettingsRoute("/settings/sage", "Sage connection", "Sage company context and master-data synchronization."),
  defineSettingsRoute("/settings/accounting", "Accounting settings", "Organization journal and posting behavior."),
  defineSettingsRoute("/settings/notifications", "Notification settings", "Operational application and email notifications."),
  defineSettingsRoute("/settings/documents", "Document settings", "Retention, file constraints, and AI availability."),
];

interface AppRouterProps {
  organizationName: string;
  pathname?: string;
  sageLocationId: string | null;
}

export function AppRouter({ organizationName, pathname, sageLocationId }: AppRouterProps) {
  const [currentPath, setCurrentPath] = useState(() => normalizePath(pathname ?? getBrowserEntryPath()));
  const context = `${organizationName} · ${sageLocationId === null ? "Top level" : `Location ${sageLocationId}`}`;
  const match = findRoute(currentPath);

  useEffect(() => {
    if (pathname !== undefined) return;

    if (window.location.pathname === "/") {
      window.history.replaceState(null, "", normalizePath(getBrowserEntryPath()));
    }
    const onPopState = () => setCurrentPath(normalizePath(window.location.pathname));
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, [pathname]);

  useEffect(() => {
    window.scrollTo({ behavior: "auto", left: 0, top: 0 });
    window.dispatchEvent(new Event("loan-management:layout-change"));
  }, [currentPath]);

  const navigate = (path: string) => {
    if (pathname !== undefined || path === currentPath) return;
    window.history.pushState(null, "", path);
    setCurrentPath(path);
  };

  return match
    ? (
      <ApplicationShell currentPath={currentPath} onNavigate={navigate}>
        <RuntimePage context={context} definition={match.definition} onNavigate={navigate} params={match.params} />
      </ApplicationShell>
    )
    : (
      <ApplicationShell currentPath={currentPath} onNavigate={navigate}>
        <RuntimePage
          context={context}
          definition={defineRoute(
            "*",
            "Page not found",
            "The requested Loan Management page does not exist.",
            "Page not found",
            "Check the address or return to the dashboard.",
          )}
          onNavigate={navigate}
          params={{}}
        />
      </ApplicationShell>
    );
}

function ApplicationShell({
  children,
  currentPath,
  onNavigate,
}: {
  children: React.ReactNode;
  currentPath: string;
  onNavigate(path: string): void;
}) {
  return (
    <div className="application-shell">
      <ApplicationNavigation currentPath={currentPath} onNavigate={onNavigate} />
      {children}
    </div>
  );
}

function RuntimePage({
  context,
  definition,
  onNavigate,
  params,
}: {
  context: string;
  definition: RouteDefinition;
  onNavigate(path: string): void;
  params: Record<string, string>;
}) {
  const identifier = params.loanId ?? params.lenderId ?? params.lenderGroupId ??
    params.postingBatchId ?? params.reconciliationRunId;
  const breadcrumbs = definition.breadcrumbs.map((breadcrumb) => ({
    ...breadcrumb,
    label: interpolateLabel(breadcrumb.label, params),
    path: interpolatePath(breadcrumb.path, params),
  }));

  return (
    <main className="page-content" id="main-content">
      <PageHeading
        breadcrumbs={breadcrumbs}
        context={context}
        description={definition.description
          ? identifier
            ? `${definition.description} Reference: ${identifier}`
            : definition.description
          : undefined}
        onNavigate={onNavigate}
        title={definition.title}
      />
      {definition.path === "/dashboard"
        ? <DashboardPage onNavigate={onNavigate} />
        : definition.path === "/lenders" || definition.path === "/lenders/:lenderId"
        ? <LendersPage lenderId={params.lenderId} />
        : definition.path === "/loans"
        ? <LoansPage onNavigate={onNavigate} />
        : definition.path === "/loans/new"
        ? <NewLoanPage onNavigate={onNavigate} />
        : definition.path === "/loans/:loanId/edit" && params.loanId
        ? <NewLoanPage loanId={params.loanId} onNavigate={onNavigate} />
        : definition.path.startsWith("/loans/:loanId") && params.loanId
        ? <LoanDetailPage loanId={params.loanId} onNavigate={onNavigate} view={definition.path.split("/").at(-1) === ":loanId" ? "overview" : definition.path.split("/").at(-1)!} />
        : definition.path === "/posting"
        ? <PostingPage onNavigate={onNavigate} />
        : definition.path === "/posting/:postingBatchId" && params.postingBatchId
        ? <PostingBatchPage postingBatchId={params.postingBatchId} />
        : definition.path === "/reconciliation"
        ? <ReconciliationPage onNavigate={onNavigate} />
        : definition.path === "/reconciliation/:reconciliationRunId" && params.reconciliationRunId
        ? <ReconciliationRunPage reconciliationRunId={params.reconciliationRunId} />
        : definition.path === "/reports"
        ? <ReportsPage />
        : definition.path === "/settings"
        ? <SettingsOverviewPage onNavigate={onNavigate} />
        : definition.path === "/settings/sage"
        ? <SageSettingsPage />
        : definition.path === "/settings/accounting"
        ? <AccountingSettingsPage />
        : <EmptyState title={definition.emptyTitle}>{definition.emptyMessage}</EmptyState>}
    </main>
  );
}

function defineRoute(
  path: string,
  title: string,
  description: string,
  emptyTitle: string,
  emptyMessage: string,
  parentLabels: string[] = [],
): RouteDefinition {
  const breadcrumbs: BreadcrumbItem[] = [
    { label: "Loan Management", path: "/dashboard" },
    ...parentLabels.map((label) => ({ label, path: `/${label.toLowerCase().replaceAll(" ", "-")}` })),
    { label: title },
  ];

  return { breadcrumbs, description, emptyMessage, emptyTitle, path, title };
}

function defineLoanRoute(path: string, title: string, description: string): RouteDefinition {
  return {
    breadcrumbs: [
      { label: "Loan Management", path: "/dashboard" },
      { label: "Loans", path: "/loans" },
      { label: ":loanId", path: "/loans/:loanId" },
      { label: title },
    ],
    description,
    emptyMessage: "Loan details will appear after the loan-core feature is configured.",
    emptyTitle: `${title} unavailable`,
    path,
    title,
  };
}

function defineSettingsRoute(path: string, title: string, description: string): RouteDefinition {
  return {
    breadcrumbs: [
      { label: "Loan Management", path: "/dashboard" },
      { label: "Settings", path: "/settings" },
      { label: title },
    ],
    description,
    emptyMessage: "These settings are introduced by a later feature and are not yet configurable.",
    emptyTitle: `${title} unavailable`,
    path,
    title,
  };
}

function interpolateLabel(label: string, params: Readonly<Record<string, string | undefined>>): string {
  return label.startsWith(":") ? params[label.slice(1)] ?? "Record" : label;
}

function interpolatePath(
  path: string | undefined,
  params: Readonly<Record<string, string | undefined>>,
): string | undefined {
  if (!path) return undefined;

  return Object.entries(params).reduce(
    (resolvedPath, [name, value]) => resolvedPath.replace(`:${name}`, value ?? ""),
    path,
  );
}

function findRoute(pathname: string): { definition: RouteDefinition; params: Record<string, string> } | null {
  for (const definition of routeDefinitions) {
    const params = matchPath(definition.path, pathname);
    if (params) return { definition, params };
  }
  return null;
}

function matchPath(pattern: string, pathname: string): Record<string, string> | null {
  const patternSegments = pattern.split("/").filter(Boolean);
  const pathSegments = pathname.split("/").filter(Boolean);
  if (patternSegments.length !== pathSegments.length) return null;

  const params: Record<string, string> = {};
  for (let index = 0; index < patternSegments.length; index += 1) {
    const patternSegment = patternSegments[index]!;
    const pathSegment = pathSegments[index]!;
    if (patternSegment.startsWith(":")) {
      try {
        params[patternSegment.slice(1)] = decodeURIComponent(pathSegment);
      } catch {
        return null;
      }
    } else if (patternSegment !== pathSegment) {
      return null;
    }
  }
  return params;
}

function normalizePath(pathname: string): string {
  return pathname === "/" ? "/dashboard" : pathname.replace(/\/+$/u, "") || "/dashboard";
}

function getBrowserEntryPath(): string {
  const requestedRoute = new URLSearchParams(window.location.search).get("route");
  return requestedRoute !== null && isEmbeddedRoute(requestedRoute)
    ? requestedRoute
    : window.location.pathname;
}